import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Button,
  DatePicker,
  Dropdown,
  Select,
  App,
  Spin,
  Switch,
  Table,
  Tooltip
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  CalendarOutlined,
  CarOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  EditOutlined,
  FileTextOutlined,
  SaveOutlined,
  FilePdfOutlined,
  MoreOutlined,
  PlusOutlined,
  DeleteOutlined,
  SendOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import WarehouseCorrectionPanel from '../modules/warehouse/WarehouseCorrectionPanel';
import BlockedActionsAlert from '../modules/warehouse/BlockedActionsAlert';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import { saveWZCorrection } from '../modules/warehouse/warehouseCorrectionApi';
import { confirmWZQuantityWarning } from '../modules/warehouse/wzReleaseFlow';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  buildWZLineSourcePayload,
  getLineTruckQuantity,
  getWZLineSourceMode,
  resetWZLineSourceToFifo,
} from '../modules/warehouse/wzLineSource';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  extractWarehouseQuantitySetPatch,
  formatWarehouseQuantity,
  getWarehouseLineUnit,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { UNIT_PRICE_INPUT_PROPS, UNIT_PRICE_MIN } from '../utils/money';
import { formatConversionValue } from '../utils/dimensionConversion';
import {
  WZItem,
  WZStatus,
  UpdateWZDto
} from '../types/wz';
import {
  WZDocumentDto,
  attachWZItemDimensions,
  buildRecipientFieldsFromCustomer,
  normalizeWZDocument,
  prepareWZItemsForSave
} from '../dto/wz';
import { OrderOptionDto, normalizeOrderOptions } from '../dto/order';
import { canManageWZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getEditableWZStatusOptions, isWZEditable, WZ_CORRECTION_REASON_MIN_LENGTH } from '../utils/wzEditRules';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import CustomerNameCombobox from '../components/inventory/CustomerNameCombobox';
import { buildWzVariantAutofill } from '../utils/wzVariantSelection';
import type { ProductVariant } from '../types/pz';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import type { TransportRouteSummaryDto } from '../dto/transport';
import { mergeWZTransportOptions } from '../utils/wzTransportSelection';
import WZTransportSummary from '../modules/wz/components/WZTransportSummary';
import WZBatchConversionMenu from '../modules/wz/components/WZBatchConversionMenu';
import {
  convertWZItemsToTarget,
  type WZBatchConversionTarget,
} from '../modules/wz/wzBatchQuantityConversion';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import { WZ_QUANTITY_MAX } from '../utils/warehouseQuantity';
import { getWZErrorMessage, isBlockingWZError, WZ_ERROR_CODE_KEYS } from '../modules/warehouse/wzErrorMessages';
import { askWarehouseSaveIntent } from '../modules/warehouse/warehouseSaveIntent';
import { askWZReleaseDate, releaseWZDocument } from '../modules/warehouse/wzReleaseFlow';
import { moveOrderAfterWzRelease } from '../modules/orders/process/orderStatusAfterWzRelease';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { useFeedback } from '../utils/feedback';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { formatCurrencyUnitPrice } from '../utils/currency';
import { formatUnit } from '../utils/units';
import { useBackNavigation } from '../hooks/useBackNavigation';
import {
  ObjectCardFacts,
  ObjectCardHero,
  ObjectCardSection,
  type ObjectCardFact,
} from '../components/layout/ObjectCard';
import { layoutCardSlots, type CardSlot } from '../components/layout/DocumentCard';
import './WZItemsView.css';
import './WZDetail.css';

const { Option } = Select;
const { TextArea } = Input;
const EMPTY_VALUE = '—';
const createEmptyItem = (): Partial<WZItem> => ({ productName: '', quantity: 1, unit: 'pcs' });

const formatLineDimensions = (item: Partial<WZItem>) =>
  formatProductDimensions({
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
  }) || '-';

const WZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const location = useLocation();
  const { t, i18n } = useTranslation(['wz', 'common', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();

  const queryParams = new URLSearchParams(location.search);
  const requestedEditMode = queryParams.get('mode') === 'edit';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wzDocument, setWzDocument] = useState<WZDocumentDto | null>(null);
  const [items, setItems] = useState<Partial<WZItem>[]>([createEmptyItem()]);
  const [orders, setOrders] = useState<OrderOptionDto[]>([]);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [linkedTransports, setLinkedTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canViewDocumentSalePrice = authService.canViewWZDocumentSalePrices();
  const canEditDocumentSalePrice = authService.canEditWZDocumentSalePrices();
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const currentUserDisplayName = currentUser?.name || '';
  // Backend-computed lock (GET /wz/:id → blockedActions `edit`). An invoiced
  // released WZ stays correctable since WP1: saving asks for the audited
  // WZ_CORRECTED_AFTER_INVOICE confirmation instead.
  const editBlocked = findBlockedAction(wzDocument?.blockedActions, 'edit');
  const canEditDocument = canEdit && isWZEditable(wzDocument?.status) && !editBlocked;
  const isEditMode = requestedEditMode && canEditDocument;
  // The „Zamówienie” field is mounted only in edit mode; the read-only card
  // takes the order from the document, so its transports still load.
  const watchedOrderId = Form.useWatch('orderId', form);
  const selectedOrderId = isEditMode ? watchedOrderId : wzDocument?.orderId;
  // Buyer address is optional on a WZ (decision 2026-09-24-wz-adres-dostawy);
  // without it the delivery address is the document's only destination, so
  // its input is always shown and required.
  const recipientAddressValue = Form.useWatch('recipientAddress', form);
  const recipientAddressMissing = !String(recipientAddressValue || '').trim();
  const showDeliveryInput = deliveryDifferent || recipientAddressMissing;
  const backgroundRefreshAllowed = !requestedEditMode && !saving;
  const backgroundRefreshAllowedRef = useRef(backgroundRefreshAllowed);
  backgroundRefreshAllowedRef.current = backgroundRefreshAllowed;
  // Goods always leave from the warehouse (decision 2026-10-04): the line
  // source is shown, never chosen here. Saving a draft keeps a legacy truck
  // line and an unchanged picked line; a changed picked line becomes FIFO.
  const isDraftEdit = isEditMode
    && wzDocument?.status !== WZStatus.RELEASED
    && wzDocument?.status !== WZStatus.RECEIVED;

  useEffect(() => {
    fetchWZDocument();
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const orderIdForTransports = String(selectedOrderId || '').trim();

    if (!orderIdForTransports) {
      setTransports(linkedTransports);
      return () => { cancelled = true; };
    }

    const fetchTransports = async () => {
      try {
        const result = await fetchOrderTransports(orderIdForTransports);
        if (!cancelled) {
          setTransports(mergeWZTransportOptions(result.transports, linkedTransports));
        }
      } catch (error: any) {
        console.error('Error fetching order transports:', error);
        if (!cancelled) {
          setTransports(linkedTransports);
        }
      }
    };

    fetchTransports();
    return () => { cancelled = true; };
  }, [selectedOrderId, linkedTransports]);

  const fetchWZDocument = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const response = await api.get(`/wz/${id}`);
      if (background && !backgroundRefreshAllowedRef.current) return;
      const doc = normalizeWZDocument(response.data, 'wz.detail.document');
      setWzDocument(doc);

      // Set form values
      form.setFieldsValue({
        correctionReason: undefined,
        ...doc,
        receivedBy: doc.receivedBy?.trim() || '',
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : undefined,
        releaseDate: doc.releaseDate ? dayjs(doc.releaseDate) : undefined,
        receivedDate: doc.receivedDate ? dayjs(doc.receivedDate) : undefined,
        orderId: doc.orderId,
        transportIds: doc.transportIds || [],
      });
      const docLinkedTransports = Array.isArray(doc.linkedTransports) ? doc.linkedTransports : [];
      setLinkedTransports(docLinkedTransports);
      setTransports((previous) => mergeWZTransportOptions(previous, docLinkedTransports));

      // The delivery address input only shows when it actually diverges from
      // the recipient address — a WZ saved with delivery == recipient opens
      // with the toggle off (backend already treats empty deliveryAddress as
      // "use recipientAddress", see handleSave below).
      const loadedDeliveryAddress = (doc.deliveryAddress || '').trim();
      const loadedRecipientAddress = (doc.recipientAddress || '').trim();
      setDeliveryDifferent(loadedDeliveryAddress !== '' && loadedDeliveryAddress !== loadedRecipientAddress);

      // Attach the linked order's product dimensions so the per-line m²/m³
      // conversion can be shown/recomputed in the form (mirrors PDF resolution).
      setItems(doc.itemsLoaded
        ? attachWZItemDimensions(doc.items, doc.orderProducts)
        : [createEmptyItem()]);
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.fetchFailed'));
      navigate('/app/wz');
    } finally {
      if (!background) setLoading(false);
    }
  };

  usePageAutoRefresh(() => fetchWZDocument(true), { enabled: backgroundRefreshAllowed });

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(normalizeOrderOptions(response.data, 'wz.detail.orders'));
    } catch (error: any) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleOrderChange = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!wzDocument || orderId !== wzDocument.orderId) {
      setLinkedTransports([]);
    }
    form.setFieldsValue({
      transportIds: [],
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    });

    if (order) {
      form.setFieldsValue({
        recipientName: order.customer?.name || '',
        recipientAddress: order.customer?.address || '',
        recipientPhone: order.customer?.phone || '',
        recipientEmail: order.customer?.email || '',
      });

      // Deliberately no item seeding here: a WZ is created only from the
      // customer order card (WZNew.tsx, `wz/new?orderId=`), and order-driven
      // item seeding has exactly one live home: the WZ create domain seam,
      // which reads GET /wz/prefill/:orderId for the remaining-quantity-netted,
      // variant-linked item list. See frontend/AGENTS.md.
    }
  };

  const isCorrection = wzDocument?.status === WZStatus.RELEASED || wzDocument?.status === WZStatus.RECEIVED;

  const handleSave = async (values: any) => {
    if (!canEditDocument) {
      feedback.error(t('wz:error.saveForbidden'), { sticky: true });
      return;
    }

    if (wzDocument && !wzDocument.itemsLoaded) {
      feedback.error(t('wz:error.itemsUnavailable'), { sticky: true });
      return;
    }

    // Saving a WZ is a two-part decision (save + move the stock?), so it is
    // asked once, here, instead of sending the operator back into the saved
    // document for a second click. See modules/warehouse/warehouseSaveIntent.
    const saveIntent = isCorrection ? 'DRAFT' : await askWarehouseSaveIntent({ documentType: 'WZ', modal, t });
    if (!saveIntent) {
      return;
    }

    setSaving(true);
    try {
      const safeItems = prepareWZItemsForSave(items).map((item) => {
        const {
          totalValue, notes, sourceMode, sourceTransportId, sourcePZDocumentId, sourceQuantity, ...safeItem
        } = item;
        // A released WZ keeps the source recorded at release (the backend
        // carries it), so a correction sends no source fields at all.
        return isCorrection
          ? safeItem
          : { ...safeItem, ...buildWZLineSourcePayload({ ...safeItem, sourceMode, sourceTransportId, sourcePZDocumentId, sourceQuantity }) };
      });
      const data: UpdateWZDto = {
        ...values,
        ...(isCorrection ? { expectedUpdatedAt: wzDocument?.updatedAt } : {}),
        wzNumber: values.wzNumber?.trim() || undefined,
        issueDate: values.issueDate?.format('YYYY-MM-DD'),
        // Release date is operator-editable only as a correction of a released
        // WZ (owner decision 2026-09-18); a draft gets it on release.
        releaseDate: isCorrection && values.releaseDate
          ? values.releaseDate.format('YYYY-MM-DD')
          : (wzDocument?.releaseDate || values.issueDate?.format('YYYY-MM-DD')),
        receivedDate: values.receivedDate ? values.receivedDate.format('YYYY-MM-DD') : null,
        transportIds: transports
          .filter((transport) => transport.status !== 'CANCELLED')
          .map((transport) => transport.id),
        items: safeItems,
        // The delivery input is hidden (and its stored value stale) whenever
        // the "different than registered" toggle is off — force it empty so
        // the backend falls back to recipientAddress instead of persisting a
        // leftover value from before the toggle was switched off.
        deliveryAddress: showDeliveryInput ? (values.deliveryAddress || '') : '',
      };
      delete data.status;
      delete (data as { costOfGoodsSold?: number | null }).costOfGoodsSold;
      delete data.producerInvoice;
      delete data.purchaseInvoiceAmount;
      delete data.transportPrice;
      delete data.transportInvoiceNo;
      delete data.transportCompany;
      delete data.driverName;
      delete data.transportNotes;

      if (isCorrection && id) {
        const saved = await saveWZCorrection(id, data as unknown as Record<string, unknown>,
          warning => confirmWZQuantityWarning(warning, modal, t, i18n.language),
          (request, body) => submitWithOperationalException(request, body, modal, t));
        if (!saved) return;
      } else {
        await api.put(`/wz/${id}`, data);
      }
      const savedNumber = data.wzNumber || wzDocument?.wzNumber;
      feedback.success(savedNumber
        ? t('wz:success.updated', { wzNumber: savedNumber })
        : t('wz:success.updatedFallback'));
      if (saveIntent === 'POST' && id) {
        // A failed or cancelled release leaves the document saved as a draft
        // — the operator retries from "Rozlicz wydanie". The release date is
        // asked at release (owner decision 2026-10-14); it starts from the
        // form's own release date when that is set and not in the future.
        const releaseDate = await askWZReleaseDate({ modal, t, defaultDate: data.releaseDate });
        if (releaseDate) {
          const released = await releaseWZDocument({
            wzId: id,
            wzNumber: savedNumber,
            modal,
            t,
            language: i18n.language,
            feedback,
            releaseDate,
          });
          if (released && wzDocument?.orderId) {
            const moved = await moveOrderAfterWzRelease(wzDocument.orderId, t('wz:orderToDeliveryNote', { number: savedNumber || '' }));
            if (moved === 'refused') feedback.warning(t('wz:orderToDeliveryRefused', { wzNumber: savedNumber || '' }));
          }
        }
      }
      // Stay on the document: drop ?mode=edit (back to view mode) and refresh.
      navigate(`/app/wz/${id}`, { replace: true });
      fetchWZDocument();
    } catch (error: any) {
      // Code -> Polish sentence now lives in modules/warehouse/wzErrorMessages.ts
      // so the list page and the create wizard read identically.
      feedback.error(getWZErrorMessage(error, t, 'wz:error.saveFailed'), {
        sticky: isBlockingWZError(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: WZStatus, releaseDate?: string) => {
    // The release path (including the over-delivery handshake) lives in
    // modules/warehouse/wzReleaseFlow so this button and the save-time choice
    // stay one behavior.
    if (newStatus === WZStatus.RELEASED) {
      if (!id) return;
      const released = await releaseWZDocument({
        wzId: id,
        wzNumber: wzDocument?.wzNumber,
        modal,
        t,
        language: i18n.language,
        feedback,
        releaseDate,
      });
      if (released) {
        // Same rule as „Wystaw WZ” on the ZK card (FF.10): a ZK before delivery → „W dostawie”.
        if (wzDocument?.orderId) {
          const moved = await moveOrderAfterWzRelease(wzDocument.orderId, t('wz:orderToDeliveryNote', { number: wzDocument.wzNumber || '' }));
          if (moved === 'refused') feedback.warning(t('wz:orderToDeliveryRefused', { wzNumber: wzDocument.wzNumber || '' }));
        }
        fetchWZDocument();
      }
      return;
    }

    try {
      await api.put(`/wz/${id}/status`, { status: newStatus });
      feedback.success(wzDocument?.wzNumber
        ? t('wz:success.statusUpdated', { wzNumber: wzDocument.wzNumber })
        : t('wz:success.statusUpdatedFallback'));
      fetchWZDocument();
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.statusUpdateFailed'), {
        sticky: isBlockingWZError(error),
      });
    }
  };

  // „Rozliczyć wydanie?” with „Data wydania” (default today): the WZ cost
  // lands in the month of that date (owner decision 2026-10-14).
  const confirmWZRelease = async () => {
    const releaseDate = await askWZReleaseDate({ modal, t });
    if (releaseDate) await handleStatusChange(WZStatus.RELEASED, releaseDate);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzDocument?.wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('wz:success.pdfDownloaded', { wzNumber: wzDocument?.wzNumber }));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  /** Quantity, its transaction unit, and conversionUnit are decided together
   *  in the conversion modal, so they must land in one state update. */
  const updateItemQuantity = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return {
          ...item,
          ...extractWarehouseQuantitySetPatch(patch),
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          conversionQuantity: patch.conversionQuantity,
          ...(patch.unit ? { unit: patch.unit } : {}),
          saleQuantity: patch.pricingQuantity ?? null,
          saleUnit: patch.pricingUnit ?? null,
      };
    }));
  };

  const updateItemUnitPrice = (index: number, unitPrice: number | null) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, unitPrice: unitPrice ?? undefined } : item
    )));
  };

  const handleBatchConversion = (target: WZBatchConversionTarget) => {
    const result = convertWZItemsToTarget(items, target);
    setItems(result.items);

    if (result.convertedCount === 0) {
      feedback.warning(t('wz:createForm.batchConversionNone'));
      return;
    }
    if (result.skippedCount > 0) {
      feedback.warning(t('wz:createForm.batchConversionPartial', {
        converted: result.convertedCount,
        skipped: result.skippedCount,
      }));
      return;
    }
    feedback.success(t('wz:createForm.batchConversionSuccess', { count: result.convertedCount }));
  };

  /** Autofill a WZ row from a catalog variant selection. */
  const handleVariantSelect = (index: number, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      ...buildWzVariantAutofill(updatedItems[index], variantId, variant),
      // Another product: back to the warehouse FIFO default.
      ...(updatedItems[index].productVariantId !== variantId ? resetWZLineSourceToFifo() : {}),
    };
    setItems(updatedItems);
  };

  /** Free-text edit of the product name combobox detaches any catalog link —
   *  the operator is describing a product not in the catalog (or has edited
   *  away from a previously picked one). A catalog pick right after this
   *  (handleVariantSelect, via onSelectVariant) fires as a separate event and
   *  supersedes it with the full autofill, so the two can't race. */
  const handleProductNameTyped = (index: number, productName: string) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      productName,
      productVariantId: undefined,
      ...resetWZLineSourceToFifo(),
    };
    setItems(updatedItems);
  };

  const statusTransitionOptions = wzDocument
    ? getEditableWZStatusOptions(wzDocument.status).filter(status => status !== wzDocument.status)
    : [];
  // Backend-computed reasons (GET /wz/:id → blockedActions). A blocked action
  // stays visible but disabled, with the reason as tooltip and on the card.
  const blockedActions = wzDocument?.blockedActions || [];
  const blockedFor = (action: string) => findBlockedAction(blockedActions, action);
  const blockedTooltip = (action: string) => {
    const blocked = blockedFor(action);
    return blocked ? getBlockedActionMessage(blocked, t, 'wz', WZ_ERROR_CODE_KEYS, { siblings: blockedActions }) : undefined;
  };
  const showReleaseAction = statusTransitionOptions.includes(WZStatus.RELEASED) || Boolean(blockedFor('release'));
  const showCancelAction = statusTransitionOptions.includes(WZStatus.CANCELLED) || Boolean(blockedFor('cancel'));

  if (loading) {
    return (
      <div className="obj-loading">
        <Spin size="large" />
      </div>
    );
  }

  const formatDate = (value?: string | null) => (value ? dayjs(value).format('DD.MM.YYYY') : EMPTY_VALUE);
  const statusPillClass = wzDocument?.status === WZStatus.CANCELLED
    ? 'obj-pill--danger'
    : wzDocument?.status === WZStatus.RELEASED || wzDocument?.status === WZStatus.RECEIVED
      ? 'obj-pill--success'
      : wzDocument?.status === WZStatus.CONFIRMED ? 'obj-pill--primary' : '';

  const moreItems: NonNullable<MenuProps['items']> = canDownloadWZPdf
    ? [{ key: 'pdf', icon: <FilePdfOutlined />, label: t('wz:actions.downloadPDF') }]
    : [];

  const describeSource = (item: Partial<WZItem>) => (getWZLineSourceMode(item) === 'MIX'
    ? t('wz:source.summary.mix', {
      quantity: formatWarehouseQuantity(getLineTruckQuantity(item), item.unit, i18n.language),
    })
    : t(`wz:source.summary.${getWZLineSourceMode(item).toLowerCase()}`));

  const describeConversion = (item: Partial<WZItem>) => {
    const physicalUnit = getWarehouseLineUnit(item);
    const storedPricingUnit = item.saleUnit
      ? getWarehouseLineUnit({ unit: item.saleUnit })
      : null;
    const storedPricingQuantity = Number(item.saleQuantity);
    const derivedConversion = formatConversionValue(
      item.conversionUnit,
      { ...item, unit: physicalUnit },
      Number(item.quantity) || 0,
    );
    return storedPricingUnit
      && storedPricingUnit !== physicalUnit
      && Number.isFinite(storedPricingQuantity)
      && storedPricingQuantity > 0
      ? formatWarehouseQuantity(storedPricingQuantity, storedPricingUnit, i18n.language)
      : derivedConversion;
  };

  const itemColumns: TableColumnsType<Partial<WZItem>> = [
    {
      title: t('wz:fields.productName'),
      key: 'product',
      render: (_, item) => {
        const dimensions = formatLineDimensions(item);
        return (
          <span className="doc-item" data-testid="wz-item-row">
            <span className="doc-item__name" title={item.productName || undefined}>{item.productName || EMPTY_VALUE}</span>
            {dimensions !== '-' && <span className="doc-item__meta" title={dimensions}>{dimensions}</span>}
          </span>
        );
      },
    },
    {
      title: t('wz:fields.quantity'),
      key: 'quantity',
      width: 104,
      align: 'right',
      render: (_, item) => (
        <span className="doc-amount">{formatWarehouseQuantity(item.quantity, item.unit, i18n.language)}</span>
      ),
    },
    {
      title: t('wz:fields.conversion'),
      key: 'conversion',
      width: 112,
      align: 'right',
      render: (_, item) => <span className="doc-amount">{describeConversion(item) || EMPTY_VALUE}</span>,
    },
    {
      title: t('wz:source.fieldLabel'),
      key: 'source',
      // No fixed width: the product name and the source share the slack, so the name stays
      // in view at 1024 px (staging v3.27.0 squeezed it to zero next to a 300 px source).
      // Text after a right-aligned figure: a gutter so the headers do not touch.
      className: 'wz-card__source-col',
      render: (_, item) => {
        const source = describeSource(item);
        return <span className="obj-cell" title={source}>{source}</span>;
      },
    },
    // Narrow exception (AGENTS.md): the sale unit price on a persisted WZ line.
    ...(canViewDocumentSalePrice ? [{
      title: t('wz:fields.unitPrice'),
      key: 'unitPrice',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, item: Partial<WZItem>) => (
        <span className="doc-amount">
          {item.unitPrice != null
            ? `${formatCurrencyUnitPrice(item.unitPrice, wzDocument?.currency || 'PLN')} / ${formatUnit(item.saleUnit || item.unit, i18n.language)}`
            : EMPTY_VALUE}
        </span>
      ),
    }] : []),
  ];

  const deliveryAddress = (wzDocument?.deliveryAddress || '').trim();
  const showDeliveryAddress = deliveryAddress !== '' && deliveryAddress !== (wzDocument?.recipientAddress || '').trim();
  const headerAlerts = [
    wzDocument?.pendingCostMarker?.costPending && (
      <Alert key="pending-cost" type="warning" showIcon message={t('wz:pendingPurchaseCost')} />
    ),
  ].filter(Boolean);

  const transportSlot: CardSlot = {
    key: 'transport',
    render: (wide) => (
      <ObjectCardSection
        wide={wide}
        testId="wz-section-transport"
        tone="teal"
        icon={<CarOutlined />}
        title={t('wz:sections.transport')}
        hint={t('wz:transport.autofillHint')}
      >
        <WZTransportSummary transports={transports} />
      </ObjectCardSection>
    ),
  };

  const facts: ObjectCardFact[] = [
    { key: 'recipient', label: t('wz:fields.recipientName'), icon: <UserOutlined />, value: wzDocument?.recipientName || EMPTY_VALUE, testId: 'wz-chip-recipient' },
    {
      key: 'order',
      label: t('wz:fields.order'),
      icon: <ShoppingCartOutlined />,
      value: wzDocument?.orderId ? (
        <Button type="link" className="obj-link" onClick={() => navigate(`/app/orders/${wzDocument.orderId}`)}>
          {wzDocument.displayOrderNumber || wzDocument.orderNumber || EMPTY_VALUE}
        </Button>
      ) : EMPTY_VALUE,
      testId: 'wz-chip-order',
    },
    // A released WZ shows the release date in the hero; its issue date moves to „Wydanie i odbiór” (five facts at most).
    ...(!isCorrection
      ? [{ key: 'issueDate', label: t('wz:fields.issueDate'), icon: <CalendarOutlined />, value: formatDate(wzDocument?.issueDate), testId: 'wz-chip-issue-date' }]
      : []),
    ...(isCorrection
      ? [{ key: 'releaseDate', label: t('wz:fields.releaseDate'), icon: <CalendarOutlined />, value: formatDate(wzDocument?.releaseDate), testId: 'wz-chip-release-date' }]
      : []),
    { key: 'deliveryDate', label: t('wz:fields.deliveryDate'), icon: <CalendarOutlined />, value: formatDate(wzDocument?.receivedDate), testId: 'wz-chip-delivery-date' },
    { key: 'salesInvoice', label: t('wz:fields.salesInvoiceNumber'), icon: <FileTextOutlined />, value: wzDocument?.salesInvoiceNumber || EMPTY_VALUE, testId: 'wz-chip-sales-invoice' },
  ];

  const viewSlots: Array<CardSlot | null> = [
    {
      key: 'items',
      wide: true,
      render: () => (
        <ObjectCardSection wide testId="wz-section-items" tone="blue" icon={<UnorderedListOutlined />} title={t('wz:sections.items')} count={items.length}>
          <Table<Partial<WZItem>>
            size="small"
            columns={itemColumns}
            dataSource={items}
            rowKey={(item, index) => `${item.id || item.productVariantId || 'item'}-${index}`}
            pagination={false}
            tableLayout="fixed"
            data-testid="wz-items-table"
          />
        </ObjectCardSection>
      ),
    },
    {
      key: 'recipient',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="wz-section-recipient" tone="indigo" icon={<EnvironmentOutlined />} title={t('wz:sections.recipientAndDelivery')}>
          <ObjectCardFacts
            items={[
              { label: t('wz:fields.recipientAddress'), value: wzDocument?.recipientAddress || EMPTY_VALUE },
              ...(showDeliveryAddress ? [{ label: t('wz:fields.deliveryAddress'), value: deliveryAddress }] : []),
              { label: t('wz:fields.recipientNIP'), value: wzDocument?.recipientNIP || EMPTY_VALUE },
              { label: t('wz:fields.recipientPhone'), value: wzDocument?.recipientPhone || EMPTY_VALUE },
              { label: t('wz:fields.recipientEmail'), value: wzDocument?.recipientEmail || EMPTY_VALUE },
            ]}
          />
        </ObjectCardSection>
      ),
    },
    {
      key: 'release',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="wz-section-release" tone="gray" icon={<UserOutlined />} title={t('wz:createForm.releaseSection')}>
          <ObjectCardFacts
            items={[
              ...(isCorrection ? [{ label: t('wz:fields.issueDate'), value: formatDate(wzDocument?.issueDate) }] : []),
              { label: t('wz:fields.issuedBy'), value: wzDocument?.issuedBy || EMPTY_VALUE },
              { label: t('wz:fields.vehicleRegistration'), value: wzDocument?.vehicleRegistration || EMPTY_VALUE },
              { label: t('wz:fields.receivedBy'), value: wzDocument?.receivedBy?.trim() || EMPTY_VALUE },
              { label: t('wz:fields.notes'), value: wzDocument?.notes || EMPTY_VALUE },
            ]}
          />
        </ObjectCardSection>
      ),
    },
    transportSlot,
    isCorrection && id ? {
      key: 'corrections',
      wide: true,
      render: () => (
        <WarehouseCorrectionPanel documentId={id} documentType="WZ"
          editing={false} version={wzDocument?.updatedAt} reasonMinLength={WZ_CORRECTION_REASON_MIN_LENGTH} />
      ),
    } : null,
  ];

  const editSlots: Array<CardSlot | null> = [
    isCorrection && id ? {
      key: 'correction',
      wide: true,
      render: () => (
        <WarehouseCorrectionPanel documentId={id} documentType="WZ"
          editing version={wzDocument?.updatedAt} reasonMinLength={WZ_CORRECTION_REASON_MIN_LENGTH} />
      ),
    } : null,
    {
      key: 'document',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="wz-section-document" tone="blue" icon={<FileTextOutlined />} title={t('wz:sections.basicInfo')}>
          <div className="obj-form">
            <Form.Item
              label={t('wz:fields.wzNumber')}
              name="wzNumber"
              rules={[
                { required: true, message: t('wz:validation.wzNumberRequired') },
                { max: 50, message: t('wz:validation.wzNumberMax') },
              ]}
            >
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item label={t('wz:fields.order')} name="orderId">
              <Select
                placeholder={t('wz:placeholders.selectOrder')}
                onChange={handleOrderChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                }
              >
                {orders.map(order => (
                  <Option key={order.id} value={order.id}>
                    {order.label || `${order.displayOrderNumber} - ${order.customerName || '-'}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={t('wz:fields.issueDate')}
              name="issueDate"
              rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
            >
              <DatePicker className="doc-control" format="DD.MM.YYYY" />
            </Form.Item>
            {isCorrection && (
              <Form.Item
                label={t('wz:fields.releaseDate')}
                name="releaseDate"
                rules={[{ required: true, message: t('wz:validation.releaseDateRequired') }]}
              >
                <DatePicker className="doc-control" format="DD.MM.YYYY" />
              </Form.Item>
            )}
            <Form.Item
              label={t('wz:fields.deliveryDate')}
              name="receivedDate"
              // One-line rule (DESIGN.md 2026-10-19): the hint sits in the ⓘ
              // tooltip, not under the picker of this narrow column.
              tooltip={t('wz:fields.deliveryDateHelp')}
            >
              <DatePicker className="doc-control" format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item label={t('wz:fields.salesInvoiceNumber')} name="salesInvoiceNumber">
              <Input maxLength={100} placeholder={t('wz:placeholders.salesInvoiceNumber')} />
            </Form.Item>
          </div>
        </ObjectCardSection>
      ),
    },
    {
      key: 'recipient',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="wz-section-recipient" tone="indigo" icon={<EnvironmentOutlined />} title={t('wz:sections.recipientAndDelivery')}>
          <div className="obj-form">
            <Form.Item
              label={t('wz:fields.recipientName')}
              name="recipientName"
              rules={[{ required: true, message: t('wz:validation.recipientNameRequired') }]}
            >
              <CustomerNameCombobox
                onSelectCustomer={(customer) => form.setFieldsValue(buildRecipientFieldsFromCustomer(customer))}
                placeholder={t('wz:placeholders.selectCustomer')}
              />
            </Form.Item>
            <Form.Item label={t('wz:fields.recipientAddress')} name="recipientAddress">
              <Input />
            </Form.Item>
            <Form.Item label={t('wz:fields.recipientNIP')} name="recipientNIP">
              <Input />
            </Form.Item>
            <Form.Item label={t('wz:fields.recipientPhone')} name="recipientPhone">
              <Input />
            </Form.Item>
            <Form.Item label={t('wz:fields.recipientEmail')} name="recipientEmail">
              <Input type="email" />
            </Form.Item>
            {!recipientAddressMissing && (
              <Form.Item label={t('wz:delivery.differentFromRegistered')}>
                <Switch
                  checked={deliveryDifferent}
                  aria-label={t('wz:delivery.differentFromRegistered')}
                  onChange={(checked) => {
                    setDeliveryDifferent(checked);
                    if (!checked) form.setFieldsValue({ deliveryAddress: '' });
                  }}
                />
              </Form.Item>
            )}
            {showDeliveryInput && (
              <Form.Item
                className="doc-form-stacked"
                name="deliveryAddress"
                label={t('wz:fields.deliveryAddress')}
                rules={recipientAddressMissing ? [{
                  required: true,
                  whitespace: true,
                  message: t('wz:validation.deliveryAddressRequiredNoCustomerAddress'),
                }] : []}
              >
                <TextArea
                  rows={2}
                  placeholder={t('wz:placeholders.deliveryAddress')}
                  aria-label={t('wz:fields.deliveryAddress')}
                  maxLength={500}
                />
              </Form.Item>
            )}
          </div>
        </ObjectCardSection>
      ),
    },
    {
      key: 'items',
      wide: true,
      render: () => (
        <ObjectCardSection
          wide
          className="wz-items-section"
          testId="wz-section-items"
          tone="blue"
          icon={<UnorderedListOutlined />}
          title={t('wz:sections.items')}
          count={items.length}
          actions={(
            <>
              {items.length > 1 && <WZBatchConversionMenu onConvert={handleBatchConversion} />}
              <Button size="small" icon={<PlusOutlined />} onClick={addItem}>
                {t('wz:actions.addItem')}
              </Button>
            </>
          )}
        >
          <WZItemsList compact>
            {items.map((item, index) => (
              <WZItemCard
                compact
                key={`${item.id || item.productVariantId || 'item'}-${index}`}
                index={index}
                positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                productLabel={t('wz:fields.productName')}
                product={(
                  <ProductNameCombobox
                    value={item.productName || ''}
                    onChange={(productName) => handleProductNameTyped(index, productName)}
                    onSelectVariant={(variantId, variant) => handleVariantSelect(index, variantId, variant)}
                    placeholder={t('wz:placeholders.productName')}
                  />
                )}
                action={(
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    aria-label={t('common:delete')}
                  />
                )}
              >
                <WZItemField label={t('wz:fields.dimensions')}>
                  {formatLineDimensions(item)}
                </WZItemField>
                <WZItemField label={t('wz:fields.quantity')} wide emphasis>
                  <WarehouseQuantityField
                    line={item}
                    onChange={(patch) => updateItemQuantity(index, patch)}
                    pricingQuantity={item.saleQuantity}
                    pricingUnit={item.saleUnit}
                    maxQuantity={WZ_QUANTITY_MAX}
                    unitLabel={t('wz:fields.unit')}
                    maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                    noDimensionsLabel={t('wz:conversion.noDimensions')}
                  />
                </WZItemField>
                <WZItemField label={t('wz:source.fieldLabel')}>
                  {describeSource(item)}
                  {isDraftEdit && getWZLineSourceMode(item) === 'BATCHES' && (
                    <span className="wz-card__source-hint" data-testid="wz-line-batches-reset-hint">
                      {t('wz:source.batchesResetOnSave')}
                    </span>
                  )}
                </WZItemField>
                {canViewDocumentSalePrice && (
                  <WZItemField label={t('wz:fields.unitPrice')} wide>
                    {canEditDocumentSalePrice ? (
                      <Tooltip title={t('common:unitPricePrecisionHint')}>
                        <InputNumber
                          {...UNIT_PRICE_INPUT_PROPS}
                          min={UNIT_PRICE_MIN}
                          max={99999999.99}
                          value={item.unitPrice}
                          onChange={(value) => updateItemUnitPrice(index, value)}
                          addonAfter={`${wzDocument?.currency || 'PLN'} / ${formatUnit(item.saleUnit || item.unit, i18n.language)}`}
                          placeholder={t('wz:placeholders.unitPrice')}
                          className="wz-card__control"
                        />
                      </Tooltip>
                    ) : item.unitPrice != null ? (
                      `${formatCurrencyUnitPrice(item.unitPrice, wzDocument?.currency || 'PLN')} / ${formatUnit(item.saleUnit || item.unit, i18n.language)}`
                    ) : EMPTY_VALUE}
                  </WZItemField>
                )}
              </WZItemCard>
            ))}
          </WZItemsList>
        </ObjectCardSection>
      ),
    },
    {
      key: 'release',
      render: (wide) => (
        <ObjectCardSection wide={wide} testId="wz-section-release" tone="gray" icon={<UserOutlined />} title={t('wz:createForm.releaseSection')}>
          <div className="obj-form">
            <Form.Item
              label={t('wz:fields.issuedBy')}
              name="issuedBy"
              rules={[
                { required: true, message: t('wz:validation.issuedByRequired') },
                { max: 100, message: t('wz:validation.personNameMax') },
              ]}
            >
              <Input placeholder={t('wz:placeholders.issuedBy')} />
            </Form.Item>
            <Form.Item label={t('wz:fields.vehicleRegistration')} name="vehicleRegistration">
              <Input placeholder={t('wz:placeholders.vehicleRegistration')} />
            </Form.Item>
            <Form.Item
              label={t('wz:fields.receivedBy')}
              name="receivedBy"
              rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
            >
              <Input placeholder={t('wz:placeholders.receivedBy')} />
            </Form.Item>
            <Form.Item className="doc-form-stacked" label={t('wz:fields.notes')} name="notes">
              <TextArea rows={2} placeholder={t('wz:placeholders.notes')} />
            </Form.Item>
          </div>
        </ObjectCardSection>
      ),
    },
    transportSlot,
  ];

  const showOperations = !isEditMode && wzDocument && canEdit
    && (statusTransitionOptions.length > 0 || blockedActions.length > 0);
  const releaseHint = t('wz:operations.releaseInventoryEffect');

  return (
    <div className="obj-card doc-card wz-card" data-testid="wz-card">
      <Form
        form={form}
        layout="vertical"
        className="wz-card__form"
        onFinish={handleSave}
        onFinishFailed={() => feedback.warning(t('wz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          issuedBy: currentUserDisplayName,
          receivedBy: '',
        }}
      >
        <ObjectCardHero
          testId="wz-card-header"
          kind={t('wz:card.kind')}
          name={wzDocument?.wzNumber || ''}
          avatar={<ExportOutlined />}
          backLabel={t('wz:card.back')}
          onBack={() => goBack('/app/wz')}
          badges={wzDocument?.status ? (
            <span className={`obj-pill ${statusPillClass}`.trim()} data-testid="wz-status-pill">
              {t(`wz:status.${wzDocument.status.toLowerCase()}`)}
            </span>
          ) : undefined}
          meta={[showDeliveryAddress ? deliveryAddress : wzDocument?.recipientAddress]}
          actions={(moreItems.length > 0 || (!isEditMode && canEdit && (canEditDocument || editBlocked))) ? (
            <>
              {moreItems.length > 0 && (
                <Dropdown
                  menu={{ items: moreItems, onClick: ({ key }) => { if (key === 'pdf') void handleDownloadPDF(); } }}
                  trigger={['click']}
                >
                  <Button icon={<MoreOutlined />} data-testid="wz-more-actions">
                    {t('wz:card.more')}
                  </Button>
                </Dropdown>
              )}
              {!isEditMode && canEdit && (canEditDocument || editBlocked) && (
                <Tooltip title={editBlocked ? getBlockedActionMessage(editBlocked, t, 'wz', WZ_ERROR_CODE_KEYS) : undefined}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/app/wz/${id}?mode=edit`, { replace: true })}
                    disabled={Boolean(editBlocked)}
                  >
                    {t('common:edit')}
                  </Button>
                </Tooltip>
              )}
            </>
          ) : undefined}
          facts={facts}
          alerts={headerAlerts.length > 0 ? headerAlerts : undefined}
        />

        <div className="obj-card__body">
          {layoutCardSlots(isEditMode ? editSlots : viewSlots)}
        </div>

        {isEditMode ? (
          <div className="doc-bar">
            <div className="doc-bar__actions">
              <Button onClick={() => navigate('/app/wz')}>{t('common:cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                {t('common:save')}
              </Button>
            </div>
          </div>
        ) : showOperations ? (
          <>
            {/* Why a button in the operations bar below is disabled: next to it, not in the header. */}
            <BlockedActionsAlert blockedActions={blockedActions} ns="wz" reasonKeys={WZ_ERROR_CODE_KEYS} />
            {(showReleaseAction || showCancelAction) && (
              <div className="doc-bar" data-testid="wz-status-actions">
                {showReleaseAction && (
                  <span className="doc-bar__hint" title={releaseHint}>
                    <InfoCircleOutlined aria-hidden="true" />
                    <span>{releaseHint}</span>
                  </span>
                )}
                <div className="doc-bar__actions">
                  {showCancelAction && (
                    <Tooltip title={blockedTooltip('cancel')}>
                      <Button
                        danger
                        icon={<CloseOutlined />}
                        onClick={() => handleStatusChange(WZStatus.CANCELLED)}
                        disabled={Boolean(blockedFor('cancel'))}
                      >
                        {t('wz:actions.cancel')}
                      </Button>
                    </Tooltip>
                  )}
                  {showReleaseAction && (
                    <Tooltip title={blockedTooltip('release')}>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={confirmWZRelease}
                        disabled={Boolean(blockedFor('release'))}
                      >
                        {t('wz:actions.settleRelease')}
                      </Button>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}
          </>
        ) : null}
      </Form>
    </div>
  );
};

export default WZDetail;
